import React, { useRef, useState } from "react";
import { UploadCloud, FolderOpen, Pause, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { fmtBytes } from "@/lib/api";
import { uploadFile } from "@/lib/chunkedUpload";

const EXT = /\.(mp4|mov|mts|m2ts|mxf|avi|mkv|webm|wav|mp3|aac|m4a|flac|aiff|aif)$/i;
const CONCURRENCY = 3;
const fmtEta = (s) => (s < 60 ? `${Math.round(s)}s` : s < 3600 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);

export const BulkUploader = ({ projectId, onDone }) => {
  const fileRef = useRef(null), dirRef = useRef(null);
  const queue = useRef([]), paused = useRef(false), ctrl = useRef(null);
  const meter = useRef({ samples: [], rate: 0 });
  const tick = (bytes) => {
    const m = meter.current, now = Date.now();
    m.samples.push([now, bytes]);
    while (m.samples.length && now - m.samples[0][0] > 10000) m.samples.shift();
    const span = Math.max((now - m.samples[0][0]) / 1000, 1);
    m.rate = m.samples.reduce((a, [, b]) => a + b, 0) / span;
  };
  const [, force] = useState(0);
  const [isPaused, setPaused] = useState(false);
  const rerender = () => force((n) => n + 1);

  const worker = async () => {
    for (;;) {
      const job = queue.current.find((j) => j.status === "pending");
      if (!job) return;
      job.status = "uploading"; rerender();
      try {
        await uploadFile(projectId, job.file, (p, b) => { job.pct = p; if (b) tick(b); rerender(); }, ctrl.current.signal, () => paused.current);
        job.status = "done"; job.pct = 1;
      } catch (e) { job.status = "failed"; job.error = e?.response?.data?.detail || e.message; }
      rerender();
    }
  };

  const start = async () => {
    ctrl.current = new AbortController();
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    const failed = queue.current.filter((j) => j.status === "failed").length;
    toast[failed ? "error" : "success"](failed ? `${failed} file(s) failed — retry them` : "All files uploaded — analysis queued");
    onDone && onDone();
  };

  const add = (files) => {
    const list = Array.from(files || []).filter((f) => EXT.test(f.name));
    if (!list.length) return toast.error("No supported media files found");
    const running = queue.current.some((j) => j.status === "uploading");
    queue.current.push(...list.map((file) => ({ file, status: "pending", pct: 0 })));
    rerender();
    if (!running) start();
  };

  const retry = () => { queue.current.forEach((j) => { if (j.status === "failed") { j.status = "pending"; j.error = null; } }); start(); };
  const togglePause = () => { paused.current = !paused.current; setPaused(paused.current); };

  const q = queue.current;
  const total = q.reduce((a, j) => a + j.file.size, 0);
  const sent = q.reduce((a, j) => a + j.file.size * j.pct, 0);
  const count = (s) => q.filter((j) => j.status === s).length;
  const active = q.filter((j) => j.status === "uploading" || j.status === "failed").slice(0, 30);

  return (
    <div className="space-y-3">
      <div data-testid="import-dropzone" onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="studio-card border-dashed border-2 border-[#252A3B] hover:border-[#E6B04E]/50 p-8 flex flex-col items-center justify-center transition-colors">
        <input ref={fileRef} data-testid="file-input" type="file" multiple accept="video/*,audio/*,.mts,.m2ts,.mxf" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        <input ref={dirRef} data-testid="folder-input" type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        <div className="w-14 h-14 rounded-2xl bg-[#212534] flex items-center justify-center mb-3"><UploadCloud size={26} className="text-[#E6B04E]" /></div>
        <div className="font-head font-semibold text-base">Drop footage, or pick files / a whole card folder</div>
        <div className="text-[#64748B] text-xs mt-1 font-mono">Resumable 8 MB chunks · 3 files × 4 chunk lanes in parallel · thousands of clips · multi-GB files</div>
        <div className="flex gap-2 mt-4">
          <button className="btn btn-gold" onClick={() => fileRef.current?.click()} data-testid="btn-pick-files"><UploadCloud size={15} />Files</button>
          <button className="btn btn-ghost" onClick={() => dirRef.current?.click()} data-testid="btn-pick-folder"><FolderOpen size={15} />Folder</button>
        </div>
      </div>
      {q.length > 0 && (
        <div className="studio-card p-4 space-y-3" data-testid="bulk-progress">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-mono text-xs" data-testid="bulk-counts">
              {count("done")}/{q.length} done · {count("uploading")} active · {count("pending")} queued · {count("failed")} failed · {fmtBytes(sent)} / {fmtBytes(total)}
              {count("uploading") > 0 && meter.current.rate > 0 && (
                <span className="text-[#E6B04E]" data-testid="upload-speed"> · {fmtBytes(meter.current.rate)}/s · ~{fmtEta((total - sent) / meter.current.rate)} left</span>
              )}
            </div>
            <div className="flex gap-2">
              <button className="btn btn-ghost !py-1 text-xs" onClick={togglePause} data-testid="bulk-pause">{isPaused ? <Play size={13} /> : <Pause size={13} />}{isPaused ? "Resume" : "Pause"}</button>
              {count("failed") > 0 && <button className="btn btn-ghost !py-1 text-xs" onClick={retry} data-testid="bulk-retry"><RotateCcw size={13} />Retry failed</button>}
            </div>
          </div>
          <div className="progress-track"><div className="progress-fill" style={{ width: `${total ? (sent / total) * 100 : 0}%` }} /></div>
          {active.map((j, i) => (
            <div key={i} className="flex items-center gap-3 text-xs">
              <span className="truncate flex-1 font-mono">{j.file.webkitRelativePath || j.file.name}</span>
              <span className={j.status === "failed" ? "text-[#f87171]" : "text-[#94A3B8]"} title={j.error}>{j.status === "failed" ? "failed" : `${Math.round(j.pct * 100)}%`}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
